export interface IGpsCoordinate {
    lat: number;
    lng: number;
}

export type GpsFailureReason = "" | "unsupported" | "no-metadata" | "no-gps" | "gps-empty";

export interface IGpsReadResult {
    gps: IGpsCoordinate | null;
    reason: GpsFailureReason;
}

interface ITiffBlock {
    start: number;
    end: number;
}

interface IIfdEntry {
    tag: number;
    type: number;
    count: number;
    valueOffset: number;
    rawValueOffset: number;
    storedInline?: boolean;
}

interface IIfd {
    entries: IIfdEntry[];
    nextOffset: number;
}

interface IExifSegment {
    start: number;
    end: number;
    tiffStart: number;
    littleEndian: boolean;
}

const TIFF_TYPE_BYTE_SIZE: { [type: number]: number } = {
    1: 1,
    2: 1,
    3: 2,
    4: 4,
    5: 8,
    6: 1,
    7: 1,
    8: 2,
    9: 4,
    10: 8,
    11: 4,
    12: 8,
};

const TIFF_TYPE_UNSIGNED_RATIONAL = 5;
const TIFF_TYPE_SIGNED_RATIONAL = 10;

const TAG_EXIF_IFD_POINTER = 0x8769;
const TAG_GPS_IFD_POINTER = 0x8825;

const GPS_TAG_LATITUDE_REF = 0x0001;
const GPS_TAG_LATITUDE = 0x0002;
const GPS_TAG_LONGITUDE_REF = 0x0003;
const GPS_TAG_LONGITUDE = 0x0004;

const MAX_LATITUDE = 90;
const MAX_LONGITUDE = 180;
const MAX_MINUTE_SECOND = 60;

// upper bound for the signature scan, real GPS blocks live in the first few hundred bytes
const MAX_GPS_SCAN_BYTES = 4096;

// a real GPS IFD carries 15-25 tags (altitude, timestamp, satellites, dop, ...)
const MAX_GPS_IFD_ENTRIES = 64;
const MIN_GPS_IFD_ENTRIES = 4;

const EXIF_HEADER = "Exif";
// the XMP APP1 header is an URI: scheme + XMP_NAMESPACE + NUL
const XMP_SCHEME_LENGTH = 7;
const XMP_NAMESPACE = "ns.adobe.com/xap/1.0/";

export default class ExifUtils {
    // Converts any EXIF rational representation into a plain number.
    // exif-js returns boxed `Number` objects ({numerator, denominator}), other
    // readers return plain numbers, {numerator, denominator}, {value} or "n/d" strings.
    public toExifNumber = (raw: any): number => {
        if (raw === null || raw === undefined || raw === "") return NaN;
        if (typeof raw === "number") return raw;
        if (typeof raw === "string") {
            const parts = raw.split("/");
            if (parts.length > 1) {
                const numerator = parseFloat(parts[0]);
                const denominator = parseFloat(parts[1]);
                return denominator ? numerator / denominator : NaN;
            }
            return parseFloat(raw);
        }
        if (typeof raw === "object") {
            if (raw.numerator !== undefined && raw.denominator !== undefined) {
                return raw.denominator ? raw.numerator / raw.denominator : NaN;
            }
            if (raw.value !== undefined) return this.toExifNumber(raw.value);
        }
        return Number(raw);
    };

    // Converts a GPS degree/minute/second array into a signed decimal degree.
    public gpsToDecimal = (gpsArray: any, ref?: any): number | null => {
        if (!Array.isArray(gpsArray) || gpsArray.length < 3) return null;

        let degree = this.toExifNumber(gpsArray[0]);
        let minute = this.toExifNumber(gpsArray[1]);
        let second = this.toExifNumber(gpsArray[2]);

        if (!this.isValidDegree(degree, minute, second)) return null;

        // writers round 59.9997 up to "60/1", so the overflow is carried instead of rejected
        minute += Math.floor(second / MAX_MINUTE_SECOND);
        second = second % MAX_MINUTE_SECOND;
        degree += Math.floor(minute / MAX_MINUTE_SECOND);
        minute = minute % MAX_MINUTE_SECOND;

        let decimal = degree + minute / 60 + second / 3600;
        const direction = typeof ref === "string" ? ref.trim().charAt(0).toUpperCase() : "";

        if (direction === "S" || direction === "W") decimal = -decimal;

        return decimal;
    };

    public isValidCoordinate = (lat: any, lng: any): boolean => {
        return (
            Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= MAX_LATITUDE && Math.abs(lng) <= MAX_LONGITUDE
        );
    };

    // Reads GPS from an image file without depending on any EXIF library.
    // `debug` logs what the reader could see when the coordinates cannot be resolved.
    public readGpsFromFile = (file: any, debug = false): Promise<IGpsCoordinate | null> => {
        return this.readFileBuffer(file).then((buffer) => {
            if (!buffer) return null;

            try {
                const gps = this.readGpsFromBuffer(buffer);
                if (!gps && debug) console.warn("[ExifUtils] GPS not resolved", this.getGpsDebugInfo(buffer));
                return gps;
            } catch (err) {
                if (debug) console.warn("[ExifUtils] GPS read failed", err);
                return null;
            }
        });
    };

    // Single pass read that also tells apart "no GPS at all" from "this file cannot be
    // read", so the caller can tell the user something actionable instead of one generic warning.
    public resolveGpsFromFile = (file: any): Promise<IGpsReadResult> => {
        return this.readFileBuffer(file).then((buffer) => {
            if (!buffer) return { gps: null, reason: "unsupported" };

            try {
                const gps = this.readGpsFromBuffer(buffer);
                if (gps) return { gps, reason: "" };

                const info = this.getGpsDebugInfo(buffer);
                if (info.container === "unknown") return { gps: null, reason: "unsupported" };
                if (!info.exifBlocks && !info.xmpPackets) return { gps: null, reason: "no-metadata" };
                // the block exists but holds no coordinates, so the photo itself has no fix
                if (info.emptyGpsBlock) return { gps: null, reason: "gps-empty" };

                // the block is readable but the coordinates are not, so the raw layout is dumped
                // to the console to make the unsupported shape visible instead of guessing
                if (!import.meta.env.PROD) console.warn("[ExifUtils] GPS not resolved", info);

                return { gps: null, reason: "no-gps" };
            } catch (err) {
                console.warn("[ExifUtils] GPS read failed", err);
                return { gps: null, reason: "no-gps" };
            }
        });
    };

    private readFileBuffer = (file: any): Promise<any | null> => {
        return new Promise((resolve) => {
            if (!file || typeof FileReader === "undefined") {
                resolve(null);
                return;
            }

            const reader = new FileReader();
            reader.onload = (event: any) => resolve(event.target.result);
            reader.onerror = () => resolve(null);
            reader.readAsArrayBuffer(file);
        });
    };

    // Dumps what the reader could see, used to diagnose unreadable GPS blocks.
    public getGpsDebugInfo = (buffer: any): any => {
        const info: any = { container: "unknown", exifBlocks: 0, gpsIfds: [], xmpPackets: 0, xmpGps: null, emptyGpsBlock: false };

        if (!buffer) return info;

        try {
            const view = new DataView(buffer);
            const container = this.detectContainer(view);
            info.container = container || "unknown";

            const blocks: ITiffBlock[] = [];
            const xmpPackets: string[] = [];

            if (container === "jpeg") {
                let offset = 2;
                while (offset + 4 <= view.byteLength) {
                    if (view.getUint8(offset) !== 0xff) break;

                    const marker = view.getUint8(offset + 1);
                    if (marker === 0xda || marker === 0xd9) break;
                    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
                        offset += 2;
                        continue;
                    }

                    const segmentLength = view.getUint16(offset + 2, false);
                    if (segmentLength < 2 || offset + 2 + segmentLength > view.byteLength) break;

                    if (marker === 0xe1) {
                        const start = offset + 4;
                        const end = offset + 2 + segmentLength;
                        if (this.matchesAscii(view, start, EXIF_HEADER)) {
                            blocks.push({ start, end });
                        } else if (this.matchesXmpHeader(view, start)) {
                            xmpPackets.push(this.readXmpPayload(view, start, end));
                        }
                    }

                    offset += 2 + segmentLength;
                }
            } else if (container === "png" || container === "webp") {
                const located = this.listTiffBlocks(view, container);
                located.blocks.forEach((block) => blocks.push(block));
                xmpPackets.push(...located.xmpPackets);
            }

            info.exifBlocks = blocks.length;
            info.xmpPackets = xmpPackets.length;

            blocks.forEach((block) => {
                const segment = this.resolveTiffSegment(view, block.start, block.end);
                if (segment) info.gpsIfds.push(...this.describeGpsIfds(view, segment));
            });

            if (xmpPackets.length) info.xmpGps = this.readGpsFromXmp(xmpPackets.join(" "));
        } catch (err) {
            info.error = (err as any).message;
        }

        info.emptyGpsBlock = info.gpsIfds.some((ifd: any) => this.isEmptyCoordinateBlock(ifd));

        return info;
    };

    // A camera app can reserve the GPS block and write no coordinates into it, which leaves
    // 0/0 rationals behind. That is a photo captured without a location fix, not a photo from
    // a device that cannot read GPS, so the two cases need different advice for the user.
    private isEmptyCoordinateBlock = (gpsIfd: any): boolean => {
        const entries: any[] = gpsIfd?.entries || [];
        const coordinates = entries.filter((entry) => entry.tagId === GPS_TAG_LATITUDE || entry.tagId === GPS_TAG_LONGITUDE);
        if (!coordinates.length) return false;

        // a real coordinate always leaves at least one finite, non zero rational behind
        return coordinates.every((entry) => !(entry.rationals || []).some((value: any) => Number.isFinite(value) && value !== 0));
    };

    public readGpsFromBuffer = (buffer: any): IGpsCoordinate | null => {
        if (!buffer) return null;

        const view = new DataView(buffer);
        const container = this.detectContainer(view);

        if (container === "jpeg") return this.readGpsFromJpeg(view);
        if (container === "png") return this.readGpsFromPng(view);
        if (container === "webp") return this.readGpsFromWebp(view);

        return null;
    };

    // -----------------------------
    // CONTAINER READERS
    // -----------------------------
    private detectContainer = (view: DataView): string => {
        if (view.byteLength < 12) return "";

        // byte wise, the magic numbers are not read as little endian words
        if (view.getUint8(0) === 0xff && view.getUint8(1) === 0xd8) return "jpeg";
        if (
            view.getUint8(0) === 0x89 &&
            view.getUint8(1) === 0x50 &&
            view.getUint8(2) === 0x4e &&
            view.getUint8(3) === 0x47 &&
            view.getUint8(4) === 0x0d &&
            view.getUint8(5) === 0x0a &&
            view.getUint8(6) === 0x1a &&
            view.getUint8(7) === 0x0a
        ) {
            return "png";
        }
        if (
            view.getUint8(0) === 0x52 &&
            view.getUint8(1) === 0x49 &&
            view.getUint8(2) === 0x46 &&
            view.getUint8(3) === 0x46 &&
            view.getUint8(8) === 0x57 &&
            view.getUint8(9) === 0x45 &&
            view.getUint8(10) === 0x42 &&
            view.getUint8(11) === 0x50
        ) {
            return "webp";
        }

        return "";
    };

    private readGpsFromJpeg = (view: DataView): IGpsCoordinate | null => {
        const xmpPackets: string[] = [];
        let offset = 2;

        while (offset + 4 <= view.byteLength) {
            if (view.getUint8(offset) !== 0xff) break;

            const marker = view.getUint8(offset + 1);
            // start of scan / end of image → no metadata beyond this point
            if (marker === 0xda || marker === 0xd9) break;
            // standalone markers without a length field
            if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
                offset += 2;
                continue;
            }

            const segmentLength = view.getUint16(offset + 2, false);
            if (segmentLength < 2 || offset + 2 + segmentLength > view.byteLength) break;

            if (marker === 0xe1) {
                const start = offset + 4;
                const end = offset + 2 + segmentLength;

                if (this.matchesAscii(view, start, EXIF_HEADER)) {
                    const segment = this.readExifSegment(view, start, end);
                    if (segment) {
                        const gps = this.readGpsFromSegment(view, segment);
                        if (gps) return gps;
                    }
                } else if (this.matchesXmpHeader(view, start)) {
                    xmpPackets.push(this.readXmpPayload(view, start, end));
                }
            }

            offset += 2 + segmentLength;
        }

        return this.readGpsFromXmpPackets(xmpPackets);
    };

    private readGpsFromPng = (view: DataView): IGpsCoordinate | null => {
        return this.readGpsFromLocatedBlocks(view, "png");
    };

    private readGpsFromWebp = (view: DataView): IGpsCoordinate | null => {
        return this.readGpsFromLocatedBlocks(view, "webp");
    };

    private readGpsFromLocatedBlocks = (view: DataView, container: string): IGpsCoordinate | null => {
        const { blocks, xmpPackets } = this.listTiffBlocks(view, container);

        for (const block of blocks) {
            const gps = this.readGpsFromTiffBlock(view, block.start, block.end);
            if (gps) return gps;
        }

        return this.readGpsFromXmpPackets(xmpPackets);
    };

    // -----------------------------
    // XMP READER
    // -----------------------------
    private matchesXmpHeader = (view: DataView, start: number): boolean => {
        return this.matchesAscii(view, start + XMP_SCHEME_LENGTH, XMP_NAMESPACE);
    };

    // the packet is bounded by its own APP1 segment, reading past it would pull the
    // compressed image data into the decoded text
    private readXmpPayload = (view: DataView, start: number, end: number): string => {
        return this.readAsciiRange(view, start + XMP_SCHEME_LENGTH + XMP_NAMESPACE.length + 1, end);
    };

    private readGpsFromXmpPackets = (packets: string[]): IGpsCoordinate | null => {
        for (const packet of packets) {
            const gps = this.readGpsFromXmp(packet);
            if (gps) return gps;
        }

        return null;
    };

    // Cameras, drones and editors often drop the EXIF GPS block and only keep XMP.
    public readGpsFromXmp = (xmp: any): IGpsCoordinate | null => {
        if (!xmp || typeof xmp !== "string") return null;

        const lat = this.readXmpCoordinate(xmp, "GPSLatitude");
        const lng = this.readXmpCoordinate(xmp, "GPSLongitude");
        if (lat === null || lng === null) return null;

        return this.isValidCoordinate(lat, lng) ? { lat: lat, lng: lng } : null;
    };

    private readXmpCoordinate = (xmp: string, name: string): number | null => {
        const pattern = new RegExp("(?:[A-Za-z0-9_.-]+:)?" + name + "\\s*=\\s*[\"']([^\"']*)[\"']", "i");
        const match = xmp.match(pattern);
        if (!match) return null;

        return this.parseXmpCoordinate(match[1]);
    };

    private parseXmpCoordinate = (raw: any): number | null => {
        const text = String(raw === null || raw === undefined ? "" : raw).trim();
        if (!text) return null;

        // "N40 14 46.5" / "N40,14,46.5"
        const refFirst = text.match(/^([NSEW])\s*([\d.]+)(?:\s*,\s*|\s+)([\d.]+)(?:(?:\s*,\s*|\s+)([\d.]+))?$/i);
        if (refFirst) {
            return this.applyXmpRef(this.buildXmpDegrees(refFirst[2], refFirst[3], refFirst[4]), refFirst[1]);
        }

        // "40,14.775N" / "40,14,46.5N"
        const refLast = text.match(/^([\d.]+)(?:\s*,\s*|\s+)([\d.]+)(?:(?:\s*,\s*|\s+)([\d.]+))?\s*([NSEW])?$/i);
        if (refLast) {
            return this.applyXmpRef(this.buildXmpDegrees(refLast[1], refLast[2], refLast[3]), refLast[4]);
        }

        // plain decimal degrees, e.g. "+40.2462576"
        const plain = Number(text.replace(/[^0-9.eE+-]/g, ""));
        return Number.isFinite(plain) ? plain : null;
    };

    private buildXmpDegrees = (degree: any, minute: any, second: any): number => {
        const parts = [Number(degree) || 0, Number(minute) || 0, Number(second) || 0];
        return parts[0] + parts[1] / 60 + parts[2] / 3600;
    };

    private applyXmpRef = (value: number, ref: any): number => {
        const direction = typeof ref === "string" ? ref.trim().charAt(0).toUpperCase() : "";
        if (direction === "S" || direction === "W") return -Math.abs(value);
        return Math.abs(value);
    };

    // -----------------------------
    // TIFF BLOCK LOCATORS
    // -----------------------------
    private listTiffBlocks = (view: DataView, container: string): { blocks: ITiffBlock[]; xmpPackets: string[] } => {
        const blocks: ITiffBlock[] = [];
        const xmpPackets: string[] = [];

        if (container === "png") {
            let offset = 8;
            while (offset + 8 <= view.byteLength) {
                const length = view.getUint32(offset);
                const type = this.readAsciiRange(view, offset + 4, offset + 8);
                const dataStart = offset + 8;
                const dataEnd = dataStart + length;
                if (length <= 0 || dataEnd > view.byteLength) break;

                if (type === "eXIf") blocks.push({ start: dataStart, end: dataEnd });
                else if (type === "iTXt" || type === "tEXt") xmpPackets.push(this.readAsciiRange(view, dataStart, dataEnd));

                if (type === "IEND") break;
                offset = dataEnd + 4;
            }
        } else if (container === "webp") {
            let offset = 12;
            while (offset + 8 <= view.byteLength) {
                const type = this.readAsciiRange(view, offset, offset + 4);
                const length = view.getUint32(offset + 4, true);
                const dataStart = offset + 8;
                const dataEnd = dataStart + length + (length % 2);
                if (dataEnd > view.byteLength) break;

                if (type === "EXIF") blocks.push({ start: dataStart, end: dataEnd });
                else if (type === "XMP ") xmpPackets.push(this.readAsciiRange(view, dataStart, dataEnd));

                offset = dataEnd;
            }
        }

        return { blocks, xmpPackets };
    };

    private resolveTiffSegment = (view: DataView, start: number, end: number): IExifSegment | null => {
        if (this.matchesAscii(view, start, EXIF_HEADER)) return this.readExifSegment(view, start, end);

        return this.readTiffHeader(view, start, end);
    };

    private readGpsFromTiffBlock = (view: DataView, start: number, end: number): IGpsCoordinate | null => {
        const segment = this.resolveTiffSegment(view, start, end);
        if (!segment) return null;

        return this.readGpsFromSegment(view, segment);
    };

    private readExifSegment = (view: DataView, start: number, end: number): IExifSegment | null => {
        if (!this.matchesAscii(view, start, EXIF_HEADER)) return null;

        return this.readTiffHeader(view, start + 6, end);
    };

    private readTiffHeader = (view: DataView, tiffStart: number, end: number): IExifSegment | null => {
        if (tiffStart < 0 || tiffStart + 8 > end || end > view.byteLength) return null;

        const byteOrder = view.getUint16(tiffStart);
        let littleEndian: boolean;
        if (byteOrder === 0x4949) littleEndian = true;
        else if (byteOrder === 0x4d4d) littleEndian = false;
        else return null;

        if (view.getUint16(tiffStart + 2, littleEndian) !== 0x002a) return null;

        return { start: tiffStart, end, tiffStart, littleEndian };
    };

    // -----------------------------
    // EXIF GPS READER
    // -----------------------------
    private readGpsFromSegment = (view: DataView, segment: IExifSegment): IGpsCoordinate | null => {
        const { start, end, tiffStart, littleEndian } = segment;
        const gpsIfdOffsets: number[] = [];

        const collectFromPointer = (entries: IIfdEntry[] | null) => {
            if (!entries) return;
            const pointer = entries.find((item) => item.tag === TAG_GPS_IFD_POINTER);
            if (pointer) gpsIfdOffsets.push(tiffStart + this.readUint32Value(view, pointer, littleEndian));
        };

        // IFD0 → Exif sub-IFD → IFD1 (thumbnail), any of them may hold the GPS pointer
        let ifdOffset = tiffStart + view.getUint32(tiffStart + 4, littleEndian);
        let guard = 0;

        while (ifdOffset > 0 && guard < 4) {
            const ifd = this.readIfd(view, tiffStart, ifdOffset, littleEndian, end);
            if (!ifd) break;

            collectFromPointer(ifd.entries);

            const exifPointer = ifd.entries.find((item) => item.tag === TAG_EXIF_IFD_POINTER);
            if (exifPointer) {
                collectFromPointer(
                    this.readIfdEntries(view, tiffStart, tiffStart + this.readUint32Value(view, exifPointer, littleEndian), littleEndian, end)
                );
            }

            ifdOffset = ifd.nextOffset;
            guard++;
        }

        // Some writers store a wrong GPS IFD pointer, so the block is also looked up by signature
        const pointerCount = gpsIfdOffsets.length;
        this.scanForGpsIfd(view, tiffStart, end, littleEndian, gpsIfdOffsets);

        const bases = [tiffStart, start, 0];

        for (let i = 0; i < gpsIfdOffsets.length; i++) {
            const gpsIfdOffset = gpsIfdOffsets[i];
            const entries = this.readIfdEntries(view, tiffStart, gpsIfdOffset, littleEndian, end);
            if (!entries) continue;

            // blocks found by signature must also carry usable hemisphere references
            const gps = this.buildCoordinate(view, entries, littleEndian, bases, gpsIfdOffset, i >= pointerCount);
            if (gps) return gps;
        }

        return null;
    };

    // Finds IFDs that hold the canonical GPS tags, regardless of the stored pointer.
    private scanForGpsIfd = (view: DataView, tiffStart: number, end: number, littleEndian: boolean, found: number[]) => {
        const limit = Math.min(end, tiffStart + MAX_GPS_SCAN_BYTES);

        for (let offset = tiffStart; offset + 2 + MIN_GPS_IFD_ENTRIES * 12 <= limit; offset++) {
            const entryCount = view.getUint16(offset, littleEndian);
            if (entryCount < MIN_GPS_IFD_ENTRIES || entryCount > MAX_GPS_IFD_ENTRIES) continue;

            const entries = this.readIfdEntries(view, tiffStart, offset, littleEndian, end);
            if (!entries || entries.length < MIN_GPS_IFD_ENTRIES) continue;

            // only the canonical GPS layout is accepted, so the scan cannot match image data
            // the ref is 2 bytes per spec, but devices also write a single byte with no NUL,
            // so a 1 or 2 byte ASCII value is accepted
            const isRef = (tag: number) => {
                const entry = entries.find((item) => item.tag === tag);
                return !!entry && entry.type === 2 && entry.count >= 1 && entry.count <= 2;
            };
            const isDms = (tag: number) => {
                const entry = entries.find((item) => item.tag === tag);
                return (
                    !!entry &&
                    (entry.type === TIFF_TYPE_UNSIGNED_RATIONAL || entry.type === TIFF_TYPE_SIGNED_RATIONAL) &&
                    entry.count >= 3
                );
            };

            if (!isRef(GPS_TAG_LATITUDE_REF) || !isDms(GPS_TAG_LATITUDE)) continue;
            if (!isRef(GPS_TAG_LONGITUDE_REF) || !isDms(GPS_TAG_LONGITUDE)) continue;

            if (found.indexOf(offset) === -1) found.push(offset);
        }
    };

    private buildCoordinate = (
        view: DataView,
        entries: IIfdEntry[],
        littleEndian: boolean,
        bases: number[],
        gpsIfdOffset: number,
        requireRefs = false
    ): IGpsCoordinate | null => {
        const latitude = entries.find((item) => item.tag === GPS_TAG_LATITUDE);
        const longitude = entries.find((item) => item.tag === GPS_TAG_LONGITUDE);
        if (!latitude || !longitude) return null;

        const latitudeRefEntry = entries.find((item) => item.tag === GPS_TAG_LATITUDE_REF);
        const longitudeRefEntry = entries.find((item) => item.tag === GPS_TAG_LONGITUDE_REF);

        // value offsets are relative to the TIFF header, but some writers store absolute
        // or segment relative ones, so every known base is tried before giving up
        const candidateBases: number[] = [];
        bases.concat([gpsIfdOffset]).forEach((base) => {
            if (candidateBases.indexOf(base) === -1) candidateBases.push(base);
        });

        for (let i = 0; i < candidateBases.length; i++) {
            const base = candidateBases[i];
            // the hemisphere refs may be stored as an offset, so they are resolved on the
            // same base as the coordinates they belong to
            const latitudeRef = this.readAscii(view, latitudeRefEntry, base);
            const longitudeRef = this.readAscii(view, longitudeRefEntry, base);

            if (requireRefs && latitudeRef !== "N" && latitudeRef !== "S") continue;
            if (requireRefs && longitudeRef !== "E" && longitudeRef !== "W") continue;

            const latValues = this.readRationals(view, latitude, littleEndian, base);
            const lngValues = this.readRationals(view, longitude, littleEndian, base);
            // an unreadable base must not end the search, the next base may still hold the values
            if (latValues.length < 3 || lngValues.length < 3) continue;

            const lat = this.gpsToDecimal(latValues, latitudeRef);
            const lng = this.gpsToDecimal(lngValues, longitudeRef);
            if (!this.isValidCoordinate(lat, lng)) continue;
            // a wrong base happily reads unrelated bytes, so the raw triple has to look like a DMS
            if (!this.hasPlausibleDegrees(latValues, lngValues)) continue;
            // a non standard base is only trusted when the rest of the block confirms it
            if (i > 0 && !this.hasCorroboratingValues(view, entries, littleEndian, base)) continue;

            return { lat: lat as number, lng: lng as number };
        }

        return null;
    };

    // Rejects a triple that was read from unrelated bytes, so a wrong offset base
    // cannot silently turn into a valid looking coordinate.
    private hasPlausibleDegrees = (latValues: number[], lngValues: number[]): boolean => {
        const isDms = (values: number[], limit: number) => {
            const [degree, minute, second] = values;
            if (!Number.isFinite(degree) || degree < 0 || degree > limit) return false;
            if (!Number.isFinite(minute) || minute < 0 || minute > MAX_MINUTE_SECOND) return false;
            if (!Number.isFinite(second) || second < 0 || second > MAX_MINUTE_SECOND) return false;
            // writers always store whole degrees, a fractional one means the bytes are not a DMS
            return Number.isInteger(degree) || (minute === 0 && second === 0);
        };

        return isDms(latValues, MAX_LATITUDE) && isDms(lngValues, MAX_LONGITUDE);
    };

    // True when at least one more rational tag of the same block is readable on the same base.
    private hasCorroboratingValues = (view: DataView, entries: IIfdEntry[], littleEndian: boolean, base: number): boolean => {
        const isLatitudeOrLongitude = (tag: number) => tag === GPS_TAG_LATITUDE || tag === GPS_TAG_LONGITUDE;

        return entries.some((entry) => {
            if (isLatitudeOrLongitude(entry.tag)) return false;
            if (entry.type !== TIFF_TYPE_UNSIGNED_RATIONAL && entry.type !== TIFF_TYPE_SIGNED_RATIONAL) return false;

            const offset = base + entry.valueOffset;
            if (offset < 0 || offset + 8 > view.byteLength) return false;

            return view.getUint32(offset + 4, littleEndian) !== 0;
        });
    };

    // -----------------------------
    // TIFF PRIMITIVES
    // -----------------------------
    private readIfd = (view: DataView, tiffStart: number, ifdStart: number, littleEndian: boolean, limit: number): IIfd | null => {
        const entries = this.readIfdEntries(view, tiffStart, ifdStart, littleEndian, limit);
        if (!entries) return null;

        const entryCount = view.getUint16(ifdStart, littleEndian);
        const nextOffsetPosition = ifdStart + 2 + entryCount * 12;
        const nextOffset = nextOffsetPosition + 4 <= limit ? view.getUint32(nextOffsetPosition, littleEndian) : 0;

        return { entries, nextOffset: nextOffset > 0 ? tiffStart + nextOffset : 0 };
    };

    private readIfdEntries = (
        view: DataView,
        tiffStart: number,
        ifdStart: number,
        littleEndian: boolean,
        limit: number
    ): IIfdEntry[] | null => {
        if (ifdStart < 0 || ifdStart + 2 > limit || ifdStart + 2 > view.byteLength) return null;

        const entryCount = view.getUint16(ifdStart, littleEndian);
        const entries: IIfdEntry[] = [];

        for (let i = 0; i < entryCount; i++) {
            const entryOffset = ifdStart + 2 + i * 12;
            if (entryOffset + 12 > limit || entryOffset + 12 > view.byteLength) break;

            const tag = view.getUint16(entryOffset, littleEndian);
            const type = view.getUint16(entryOffset + 2, littleEndian);
            const count = view.getUint32(entryOffset + 4, littleEndian);
            const rawValueOffset = view.getUint32(entryOffset + 8, littleEndian);
            const typeSize = TIFF_TYPE_BYTE_SIZE[type];
            if (!typeSize) continue;

            const valueOffset = typeSize * count > 4 ? rawValueOffset : entryOffset + 8;

            entries.push({
                tag,
                type,
                count,
                valueOffset,
                rawValueOffset,
                storedInline: typeSize * count <= 4,
            });
        }

        return entries;
    };

    private readRationals = (view: DataView, entry: IIfdEntry, littleEndian: boolean, base: number): number[] => {
        if (entry.type !== TIFF_TYPE_UNSIGNED_RATIONAL && entry.type !== TIFF_TYPE_SIGNED_RATIONAL) return [];

        const isSigned = entry.type === TIFF_TYPE_SIGNED_RATIONAL;
        const values: number[] = [];

        for (let i = 0; i < 3; i++) {
            const offset = base + entry.valueOffset + i * 8;
            if (offset < 0 || offset + 8 > view.byteLength) return [];

            const numerator = isSigned ? view.getInt32(offset, littleEndian) : view.getUint32(offset, littleEndian);
            const denominator = isSigned ? view.getInt32(offset + 4, littleEndian) : view.getUint32(offset + 4, littleEndian);

            values.push(denominator ? numerator / denominator : NaN);
        }

        return values;
    };

    private readUint32Value = (view: DataView, entry: IIfdEntry, littleEndian: boolean): number => {
        if (entry.type === 4) return view.getUint32(entry.valueOffset, littleEndian);
        if (entry.type === 3) return view.getUint16(entry.valueOffset, littleEndian);
        return 0;
    };

    private readAscii = (view: DataView, entry?: IIfdEntry, base = 0): string => {
        if (!entry || !entry.count) return "";

        // an inline value is addressed by its slot, an offset one by its stored pointer
        const start = entry.storedInline ? entry.valueOffset : base + entry.valueOffset;
        const length = Math.min(entry.count, 4);
        let text = "";
        for (let i = 0; i < length; i++) {
            if (start + i >= view.byteLength) break;
            text += String.fromCharCode(view.getUint8(start + i));
        }

        return text.replace(/\0/g, "").trim();
    };

    private readAsciiRange = (view: DataView, start: number, end: number): string => {
        const from = Math.max(0, start);
        const to = Math.min(end, view.byteLength);
        if (to <= from) return "";

        const size = to - from;
        const decoder = typeof TextDecoder !== "undefined" ? new TextDecoder("utf-8") : null;
        if (decoder) {
            try {
                return decoder.decode(new Uint8Array(view.buffer, view.byteOffset + from, size));
            } catch (err) {
                /* fall through to the byte wise decode */
            }
        }

        let text = "";
        for (let i = from; i < to; i += 4096) {
            const chunk: number[] = [];
            for (let j = i; j < Math.min(i + 4096, to); j++) chunk.push(view.getUint8(j));
            text += String.fromCharCode.apply(null, chunk);
        }
        return text;
    };

    private isValidDegree = (degree: number, minute: number, second: number): boolean => {
        return (
            Number.isFinite(degree) &&
            Number.isFinite(minute) &&
            Number.isFinite(second) &&
            degree >= 0 &&
            minute >= 0 &&
            second >= 0
        );
    };

    private matchesAscii = (view: DataView, start: number, text: string): boolean => {
        if (start < 0 || start + text.length > view.byteLength) return false;

        for (let i = 0; i < text.length; i++) {
            if (view.getUint8(start + i) !== text.charCodeAt(i)) return false;
        }
        return true;
    };

    private describeGpsIfds = (view: DataView, segment: IExifSegment): any[] => {
        const { end, tiffStart, littleEndian } = segment;
        const offsets: number[] = [];

        const firstIfdOffset = tiffStart + view.getUint32(tiffStart + 4, littleEndian);
        const ifd = this.readIfd(view, tiffStart, firstIfdOffset, littleEndian, end);
        const pointer = ifd?.entries.find((item) => item.tag === TAG_GPS_IFD_POINTER);
        if (pointer) offsets.push(tiffStart + this.readUint32Value(view, pointer, littleEndian));

        this.scanForGpsIfd(view, tiffStart, end, littleEndian, offsets);

        return offsets.map((gpsIfdOffset) => {
            const entries = this.readIfdEntries(view, tiffStart, gpsIfdOffset, littleEndian, end) || [];
            return {
                offset: gpsIfdOffset,
                entryCount: entries.length,
                entries: entries.map((entry) => ({
                    tag: "0x" + entry.tag.toString(16).padStart(4, "0"),
                    tagId: entry.tag,
                    type: entry.type,
                    count: entry.count,
                    rawValueOffset: entry.rawValueOffset,
                    storedInline: (TIFF_TYPE_BYTE_SIZE[entry.type] || 0) * entry.count <= 4,
                    text: entry.type === 2 ? this.readAscii(view, entry, tiffStart) : undefined,
                    rationals:
                        entry.type === TIFF_TYPE_UNSIGNED_RATIONAL || entry.type === TIFF_TYPE_SIGNED_RATIONAL
                            ? this.readRationals(view, entry, littleEndian, tiffStart)
                            : undefined,
                })),
            };
        });
    };
}
